<template>
    <Loader v-if="loading"></Loader>
    <div v-else-if="alertTitle">
      <alert
          :title="alertTitle"
          :color="alertStyle"
          :text="alertText"
          :icon="alertIcon"
          :dismissable="false"
      ></alert>
    </div>
    <div v-else>
      <div class="ptop-10 pright-10 pleft-10" v-if="cartNotice">
        <div class="alert alert-warning">
          <i class="fas fa-info-circle"></i> {{ $t(cartNotice) }}
        </div>
      </div>
      <div>
        <div class="pull-right">
          <form-field type="text" :placeholder="$t('Common.searchPlaceholder')" v-model="filter" field-class="col-xs-12 no-padding"/>
        </div>
      </div>
      <div style="clear:both;">
        <DataTable :columns="columns" :data="suppliersFiltered" v-on:refresh="refreshGrid" :row-class="rowClass" :cell-class="cellClass">
          <template v-slot:default="{row, col, mode}">
            <template v-if="col.id === 'logo'">
              <div class="radio">
                <input :name="`supplier_${mode}`"
                       type="radio"
                       :key="`supplier_${row.ID}_${mode}`"
                       :id="`supplier_${row.ID}_${mode}`"
                       :value="row.ID"
                       :checked="formValues.supplier == row.ID"
                       @click="$event => setSupplier($event, row)"
                />
                <label :for="`supplier_${row.ID}_${mode}`">
                  <img class="provider-logo" :alt="row.name" :src="`${baseUrl}/${accountId}/${workflowType.ID}/${row.ID}/avatar`" />
                </label>
              </div>
            </template>
            <template v-else-if="col.id === 'name'">
              {{row.name}}
            </template>
            <template v-else-if="col.id === 'distance'">
              <template v-if="location && row.map && row.map.Latitude !== 0 && row.map.Longitude !== 0">
                {{distanceToStr(row)}}
              </template>
              <template v-else>
                N/A
              </template>
            </template>
            <template v-else-if="col.id === 'price'">
              {{ row.price | formatCurrency }}
              <a v-if="row.priceTooltip"
                 :data-balloon="$t(row.priceTooltip)"
                 :data-balloon-length="undefined"
                 :data-balloon-pos="undefined"
              >
                <i class="fa fa-info-circle"></i>
              </a>
            </template>
            <template v-else-if="col.id === 'availability'">
              <div
                  :data-balloon="row.availabilityComment || undefined"
                  :data-balloon-length="undefined"
                  :data-balloon-pos="undefined">
                <img
                    width="100"
                    :src="`${baseUrl}/${accountId}/${workflowType.ID}/${row.ID}/availability`"
                    :alt="row.availabilityComment"
                />
              </div>
            </template>
          </template>
        </DataTable>
      </div>
    </div>
</template>

<script>
  import FormsMixin from '../web-core/src/Mixins/Forms'
  import FormField from '../web-core/src/Elements/FormField'
  import DataTable from '../web-core/src/Elements/DataTable';
  import Loader from '../web-core/src/Elements/Loader';
  import {calcCrow} from './utils';
  import MyLocationMixin from './MyLocationMixin'

export default {
  components: {FormField, DataTable, Loader},
  mixins: [ FormsMixin, MyLocationMixin ],
  props: {
    forceValidation: [Boolean, Object],
    formValues: Object,
    itemNums: Object,
    itemNumsAll: Object,
    hasOrder: Boolean,
    isEditable: Boolean,
    config: Object,
    workflowType: Object,
    cartItems: [Object, Array],
    projectId: {},
    cartNotice: Object,
  },
  data() {
    return {
      // baseUrl: 'https://cloud.zeyos.com/bredent/remotecall/ready4use.api.supplier',
      loading: true,
      suppliers: [],
      location: null,
      filter: '',
      sort: 'name_asc',
      alertTitle: '',
      alertText: '',
      alertStyle: 'danger',
      alertIcon: '',
    }
  },
  computed: {
    baseUrl() {
      return this.$root.supplierUrl;
    },
    columns() {
      return [
        {
          id: 'logo',
          sortable: false,
          label: ''
        },
        {
          id: 'name',
          sortable: true,
          label: 'Common.supplierName'
        },
        {
          id: 'distance',
          sortable: true,
          label: 'Common.distance'
        },
        {
          id: 'price',
          sortable: true,
          label: 'Common.price',
          tooltip: {de: 'Preis für das gesamte Paket (exklusive Zusatzprodukte)', en: 'Price for the entire package (aside from addon products)'}
        },
        {
          id: 'availability',
          sortable: true,
          label: 'Common.availability'
        }
      ]
    },
    accountId() { return this.$root.currentUser?.ID },
    suppliersFiltered() {
      const filter = (this.filter ?? '').toLowerCase();

      const sorters = {
        distance_asc:  (a, b) => this.distanceTo(a) - this.distanceTo(b) > 0 ? 1 : -1,
        distance_desc: (a, b) => this.distanceTo(a) - this.distanceTo(b) < 0 ? 1 : -1,
        name_asc:      (a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
        name_desc:     (a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? 1 : -1,
        availability_asc:      (a, b) => a.availability > b.availability ? 1 : -1,
        availability_desc:     (a, b) => a.availability < b.availability ? 1 : -1,
        price_asc:      (a, b) => a.price > b.price ? 1 : -1,
        price_desc:     (a, b) => a.price < b.price ? 1 : -1,
      };

      return this.suppliers
          .filter(x => !filter
              || x.name.toLowerCase().indexOf(filter) >= 0
              || x.city.toLowerCase().indexOf(filter) >= 0
              || x.country.toLowerCase().indexOf(filter) >= 0
              || x.postalcode.toLowerCase().indexOf(filter) >= 0
          )
          .sort(sorters[this.sort]);
    },
  },
  async mounted() {
    this.loading = true;
    this.suppliers = [];
    await this.refresh();
    this.location = await this.fetchMyLocation();
  },
  watch: {
    cartItems(value) {
      this.cartItems = value;
      this.refresh();
    }
  },
  methods: {
    setSupplier($event, row) {
      // $event.preventDefault();
      this.handleFormInput(row.ID, {name: 'supplier'}, `supplier-${row.ID}`)
    },
    rowClass(row) {
      return this.formValues.supplier == row.ID ? 'supplier-row selected' : 'supplier-row';
    },
    cellClass(cell) {
      return cell.id;
    },
    async refresh() {
      try {
        const {body} = await this.$http.post(`${this.baseUrl}/${this.accountId}/${this.workflowType.ID}?projectID=${encodeURIComponent(this.projectId)}`, {
          items: this.cartItems,
          locale: this.$i18n.locale,
        });
        this.suppliers = body;

        this.alertTitle = '';
        this.alertText = '';
      } catch (e) {
        this.alertTitle = this.$t('ProjectDetails.errorCouldNotLoad');
        this.alertText  = this.$t('ProjectDetails.errorInvalidServerResponse');
        console.error('failed', e);
      }

      this.loading = false;

      const current = Number(this.formValues.supplier);
      if (!this.suppliers.some(x => x.ID === current)) {
        this.formValues.supplier = null;
      }

      setTimeout(() => {
        this.$emit('valid', this.formValues.supplier != null);
      }, 100)
    },
    handleSort(column) {
      console.log('handleSort', column);
      if (this.sort.startsWith(column + '_')) {
        this.sort = column + '_' + (this.sort.endsWith('asc') ? 'desc' : 'asc');
      } else {
        this.sort = column + '_asc';
      }
    },
    async refreshGrid(offset, orderby) {
      this.sort = orderby.startsWith('-') ? `${orderby.substring(1)}_desc` : `${orderby}_asc`;
      this.$forceUpdate()
    },
    handleFormInput(value, field, itemnum) {
      console.log('handleFormInput', value, field, itemnum);
      this.formValues[field.name] = value;
      this.itemNums[field.name] = itemnum;
      this.$emit('input', value, field, itemnum);
      this.$forceUpdate()
      setTimeout(() => {
        this.$forceUpdate()
        this.$emit('valid', this.formValues.supplier != null);
      }, 100)
    },
    getItemNums() {
      if (!this.formValues.supplier) {
        setTimeout(() => {
          this.$emit('valid', false);
        }, 1)

        return {section: null, items: []}
      }

      return {
        section: null,
        items: {
          [`supplier-${this.formValues.supplier}`]: 1
        }
      }
    },
    getExperience(supplier) {
      return supplier.experience[this.workflowType.ID] || 0
    },
    distanceToStr(supplier) {
      if (!this.location) {
        return 'N/A';
      }
      return this.distanceTo(supplier) + ' km';
    },
    distanceTo(supplier) {
      if (!this.location) {
        return Number.MAX_SAFE_INTEGER;
      }
      return calcCrow(...this.locationOf(supplier), this.location[0], this.location[1]).toFixed(1);
    },
    locationOf(supplier) {
      return [supplier.map.Latitude, supplier.map.Longitude];
    }
  }
}
</script>

<style scoped>
    table {
        margin: 0 -30px;
        width: calc(100% + 60px);
        border: none;
    }

    .sortable {
      cursor: pointer;
    }
</style>

<style>
    .supplier-row {
      border-bottom: 1px solid rgba(0,0,0,.3);
    }

    .supplier-row td {
      vertical-align: middle !important;
    }

    .supplier-row td:first-child {
      padding-left: 30px;
    }

    .supplier-row td:last-child {
      padding-right: 15px;
    }

    .supplier-row img {
      margin: 15px 0;
    }

    .supplier-row.selected, .supplier-row.selected .card-body{
      background-color: rgba(0,0,0,.1) !important;
      transition: background-color .3s ease-in;
    }

    .supplier-logo {
      text-align: center;
    }
</style>
